import React from "react";
import "./StudioPolicy.css";

const StudioPolicy = () => {
  return (
    <div className="studio-page">
      <div className="studio-container">
        <h1>Comprehensive Membership Agreement</h1>
        <p className="effective-date">Effective Date: July 1, 2025</p>

        <section className="studio-section">
          <div className="welcome-section">
            <p>
              Welcome to Diviti Adora! This Comprehensive Membership Agreement
              ("Agreement") governs your membership and use of services at our
              studio. It includes our policies on payments, cancellations, and
              studio use, as well as important legal terms and disclaimers. By
              creating an account, booking sessions, or making any purchase of
              services, memberships, or packages with Diviti Adora Studio, you
              acknowledge that you have read, understood, and agree to be
              legally bound by all terms and conditions of this Agreement.
            </p>
          </div>
        </section>

        <section className="studio-section">
          <h2>MEMBERSHIP TERMS & POLICIES</h2>

          <h3>1. Payment Authorization & Refund Policy</h3>
          <p>
            <strong>Agreement to Pay:</strong> You ("Member") agree to pay
            Diviti Adora Studio for the selected membership plan. You authorize
            Diviti Adora Studio to automatically charge the credit/debit card on
            file for all recurring membership fees, late fees,
            no-show/late-cancellation fees, and any other fees incurred.
          </p>
          <p>
            <strong>Refund Policy:</strong> All sales are final. Payments for
            all merchandise, services, sessions, memberships, and packages are
            non-refundable.
          </p>

          <h3>2. Membership Commitment, Renewal, and Cancellation</h3>
          <p>
            <strong>Monthly Memberships:</strong> Monthly Auto-Renew memberships
            require a minimum commitment of three (3) full billing cycles. After
            this commitment, the membership automatically continues
            month-to-month until a valid cancellation notice is provided.
          </p>
          <p>
            <strong>Early Termination Fee:</strong> If you cancel your Monthly
            membership before fulfilling the 3-month commitment, you will be
            charged an early termination fee equal to one (1) full-price month
            of your membership.
          </p>
          <p>
            <strong>Prepaid Memberships:</strong> Prepaid memberships (3, 6, and
            12-month) will automatically renew for the same term on the
            anniversary of their activation date.
          </p>
          <p>
            <strong>Cancellation Process:</strong> To cancel a Monthly
            membership (after commitment) or to opt-out of a Prepaid renewal,
            you must inform Diviti Adora in writing (email is ok) at least
            30 days prior to your next billing or renewal date. You may be
            subject to one final payment within this notice period. Verbal
            cancellations are not accepted.
          </p>

          <h3>3. Membership Pause (Hold) Policy</h3>
          <p>
            <strong>Eligibility:</strong> Monthly members who have completed
            their initial 3-month commitment may pause their membership. Prepaid
            memberships are not eligible for pauses.
          </p>
          <p>
            <strong>Duration & Fee:</strong> The pause duration is a minimum of
            one (1) month and a maximum of six (6) months. A fee of $15 per
            month will be charged on your regular billing date to hold your
            membership and lock in your rate.
          </p>
          <p>
            <strong>Process:</strong> A 30-day advance notice via the Membership
            Pause Request Form is required. Your membership and regular billing
            will automatically reactivate on the pre-agreed-upon end date.
          </p>

          <h3>4. Membership Upgrades & Downgrades</h3>
          <p>
            <strong>Upgrading:</strong> You may upgrade your membership tier at
            any time. The difference will be prorated for the current billing
            cycle.
          </p>
          <p>
            <strong>Downgrading:</strong> You may request to downgrade your
            membership tier after your initial 3-month commitment is fulfilled
            by providing 30 days' written notice via email to
            info@divitiadora.com.
          </p>
        </section>

        <section className="studio-section">
          <h2>STUDIO POLICIES, WAIVERS, AND DISCLAIMERS</h2>

          <h3>5. Health, Safety, and Medical Disclaimer</h3>
          <p>
            <strong>Medical Disclaimer:</strong> The Services offered by Diviti
            Adora Infrared & Red Light Studio are not intended to diagnose,
            treat, cure, or prevent any disease. You are strongly encouraged to
            consult with a healthcare professional before starting any new
            wellness treatment, especially if you have pre-existing health
            conditions, are pregnant, or have any other health concerns.
          </p>
          <p>
            <strong>Assumption of Risk:</strong> By using our Services, you
            acknowledge and assume the risks associated with infrared and red
            light therapies, including but not limited to changes in blood
            pressure, dehydration, and potential skin irritation.
          </p>
          <p>
            <strong>Safety Guidelines:</strong> You agree to follow all posted
            safety guidelines and instructions provided by our staff. Failure to
            adhere to these guidelines may result in injury, for which Diviti
            Adora Studio will not be held liable.
          </p>

          <h3>6. Release of Liability and Indemnification</h3>
          <p>
            <strong>Release:</strong> By creating an account, booking sessions,
            or purchasing any services, memberships, or packages from Diviti
            Adora Studio, you acknowledge and agree that all participation in
            activities, use of studio facilities, and engagement with our
            services (whether in-person or online) is voluntary and at your own
            risk. You voluntarily release, waive, and discharge Diviti Adora
            Studio, its owners, employees, contractors, and agents from any and
            all liability, claims, demands, or causes of action related to any
            injury, illness, damage, or loss you may sustain during or arising
            from such participation or use.
          </p>
          <p>
            <strong>Indemnification:</strong> You agree to indemnify, defend,
            and hold harmless Diviti Adora Studio, its owners, employees,
            contractors, and agents against any claims, liabilities, damages,
            costs, or expenses (including reasonable attorneys’ fees) resulting
            from your use of the services, breach of this Agreement, or
            violation of any applicable law or third-party rights.
          </p>
          <p>
            <strong>Electronic Consent:</strong> You acknowledge and agree that
            this waiver and release is legally binding and enforceable as if
            signed in writing. No physical signature is required, and your use
            of the studio’s services, booking of sessions, or completion of any
            purchase constitutes your acceptance of these terms in full.
          </p>

          <h3>7. Member Eligibility and Conduct</h3>
          <p>
            <strong>Eligibility:</strong> You must be at least 18 years old to
            use our Services and enter into this Agreement. You agree to provide
            accurate and current information and are responsible for all
            activities under your account.
          </p>
          <p>
            <strong>Studio Conduct:</strong> You are expected to behave
            respectfully towards staff and other patrons. Any behavior deemed
            disruptive, unsafe, or in violation of studio rules may result in
            immediate termination of your access to our Services without a
            refund.
          </p>
        </section>

        <section className="studio-section">
          <h2>CANCELLATION AND RESCHEDULING POLICY</h2>

          <h3>8. Cancellation and Rescheduling Policy</h3>
          <p>
            <strong>
              Standard Appointments (Packages, Singles, and Non-Unlimited
              Memberships):
            </strong>
            <br />
            If you need to cancel or reschedule your infrared or red light
            session, you must notify us at least 4 hours in advance. With proper
            notice, you will not receive a refund, but you will be allowed to
            reschedule your session at no additional cost.
          </p>
          <p>
            <strong>Late Cancellation:</strong>
            <br />
            If you cancel with less than 4 hours' notice, a fee of $25 per
            person will be charged. This fee applies whether or not you
            reschedule the session.
          </p>

          <h3>9. Late Arrival / No-Show Policy</h3>
          <p>
            <strong>Late Arrivals:</strong>
            <br />
            If you arrive more than 5 minutes after your scheduled session
            time, your appointment will be canceled and you will be charged a
            $25 fee per person.
          </p>
          <p>
            <strong>No-Shows:</strong>
            <br />
            Failure to attend a scheduled session without prior notice will
            similarly result in a $25 fee per person.
          </p>

          <h3>10. Refund Policy</h3>
          <p>
            <strong>No Refunds:</strong>
            <br />
            All payments made for sessions, packages, and memberships are
            non-refundable. The only available option for clients who cancel
            with the required notice is to reschedule their session.
          </p>
          <p>
            <strong>Non-Refundable Fees:</strong>
            <br />
            Any fees incurred as a result of late cancellations, no-shows, or
            early membership cancellations (before the 3-month commitment) are
            non-refundable.
          </p>
        </section>

        <section className="studio-section">
          <h2>GENERAL PROVISIONS</h2>

          <h3>11. Modification and Termination</h3>
          <p>
            Diviti Adora Studio reserves the right to modify these terms, our
            services, or our fees at any time. We will provide notice of any
            significant changes. The Studio reserves the right to terminate this
            Agreement and your membership for any reason, including failure to
            adhere to the terms outlined herein.
          </p>

          <h3>12. Governing Law</h3>
          <p>
            This Agreement shall be governed by and construed in accordance with
            the laws of the State of Arizona.
          </p>

          <h3>13. Binding Agreement & Acknowledgment</h3>
          <p>
            By completing a purchase, booking a session, or otherwise using
            Diviti Adora Studio’s services, you acknowledge and agree that this
            Agreement is legally binding and enforceable as if signed in
            writing. No physical signature is required.
          </p>
        </section>

        <section className="studio-section">
          <h3>Contact Information</h3>
          <p>
            If you have any questions or concerns regarding this Comprehensive
            Membership Agreement, please contact us at:
          </p>
          <address>
            Diviti Adora Infrared & Red Light Studio
            <br />
            520.222.7031
            <br />
            1930 E Tangerine Rd
            <br />
            Suite 100
            <br />
            Oro Valley, AZ 85737
            <br />
            Email:{" "}
            <a href="mailto:info@divitiadora.com">info@divitiadora.com</a>
          </address>
        </section>
      </div>
    </div>
  );
};

export default StudioPolicy;
